import React, { useEffect, useState } from "react";
import { DescriptionCheckbox } from "../../input/DescriptionCheckbox";
import {
  OrganizationDataRecipientDto,
  ProcessingRegion as ProcessingRegionEnum,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { Tooltip } from "@mui/material";
import { usePrimaryOrganization } from "../../../hooks";
import { InlineLoading } from "../../../../common/components/Loading";

type DataRecipientProcessingRegionsSettingProps = {
  recipient?: OrganizationDataRecipientDto;
  updateState?: (update: ProcessingRegionEnum[]) => Promise<OrganizationDataRecipientDto>;
  updateAllForRegion?: (region: ProcessingRegionEnum, checked: boolean) => Promise<boolean>;
  regionsCheckedByDefaultForUpdateAll?: ProcessingRegionEnum[];
};
// I tried very hard to make this a combined component, due to the weirdness of our network calls, I couldn't make it work so the code is pretty duped
// between the two components in this file. The main difficulty is that the GetCompliant side needs the recipient to work,
// if I pass in just recipient.processingRegion to the second component it no longer works regardless of what I've tried

// This is the Get Compliant version of the Settings row. Turns into a "select all" mode if recipient is null.
export const DataRecipientProcessingRegionsSetting = ({
  recipient,
  updateState,
  updateAllForRegion,
  regionsCheckedByDefaultForUpdateAll,
}: DataRecipientProcessingRegionsSettingProps) => {
  const [organization] = usePrimaryOrganization();
  const [loading, setLoading] = useState<boolean>(false);

  const [usChecked, setUsChecked] = useState<boolean>(
    recipient
      ? recipient.processingRegions?.includes("UNITED_STATES")
      : !!regionsCheckedByDefaultForUpdateAll?.includes("UNITED_STATES"),
  );
  const [eeaChecked, setEeaChecked] = useState<boolean>(
    recipient
      ? recipient.processingRegions?.includes("EEA_UK")
      : !!regionsCheckedByDefaultForUpdateAll?.includes("EEA_UK"),
  );
  const [canadaChecked, setCanadaChecked] = useState<boolean>(
    recipient
      ? recipient.processingRegions?.includes("CANADA")
      : !!regionsCheckedByDefaultForUpdateAll?.includes("CANADA"),
  );

  useEffect(() => {
    if (recipient) {
      setUsChecked(recipient.processingRegions.includes("UNITED_STATES"));
      setEeaChecked(recipient.processingRegions.includes("EEA_UK"));
      setCanadaChecked(recipient.processingRegions.includes("CANADA"));
    }
  }, [recipient]);

  const updateProcessingRegions = async (region: ProcessingRegionEnum, checked: boolean) => {
    if (!recipient) {
      setLoading(true); // Technically, don't really need to set/unset loading here, since we're hiding the whole page while this update happens anyway.
      await updateAllForRegion(region, checked);
      setLoading(false);
      return;
    }

    if (checked) {
      if (!recipient.processingRegions.some((r) => r == region)) {
        recipient.processingRegions.push(region);
      }
    } else {
      recipient.processingRegions = recipient.processingRegions.filter((r) => r !== region);
    }
    setLoading(true);
    try {
      await updateState(recipient.processingRegions);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ml-xxs flex-row">
      {loading && (
        <span className="mt-sm">
          <InlineLoading size="sm" />
        </span>
      )}
      <DescriptionCheckbox
        disabled={loading}
        label={
          <Tooltip title="US">
            <img src="/assets/images/flag-usa-small.png" alt="US" width="40" height="25" />
          </Tooltip>
        }
        checked={usChecked}
        onChange={(e, checked) => {
          setUsChecked(checked);
          return updateProcessingRegions("UNITED_STATES", checked);
        }}
      />
      {organization && organization.featureGdpr && (
        <DescriptionCheckbox
          disabled={loading}
          label={
            <Tooltip title="EEA/UK/CH">
              <img src="/assets/images/flag-eea.svg" alt="EEA/UK/CH" width="40" height="26" />
            </Tooltip>
          }
          checked={eeaChecked}
          onChange={(e, checked) => {
            setEeaChecked(checked);
            return updateProcessingRegions("EEA_UK", checked);
          }}
        />
      )}
      {organization && organization.featurePipeda && (
        <DescriptionCheckbox
          disabled={loading}
          label={
            <Tooltip title="Canada">
              <img src="/assets/images/flag-canada.svg" alt="Canada" width="40" height="26" />
            </Tooltip>
          }
          checked={canadaChecked}
          onChange={(e, checked) => {
            setCanadaChecked(checked);
            return updateProcessingRegions("CANADA", checked);
          }}
        />
      )}
    </div>
  );
};

type ProcessingRegionsSettingProps = {
  processingRegions: ProcessingRegionEnum[];
  updateState: (update: ProcessingRegionEnum[]) => void;
};
// This is the Stay Compliant variant of the processing region settings.
export const ProcessingRegionsSetting = ({
  processingRegions,
  updateState,
}: ProcessingRegionsSettingProps) => {
  const [organization] = usePrimaryOrganization();
  const [usChecked, setUsChecked] = useState<boolean>(processingRegions.includes("UNITED_STATES"));
  const [eeaChecked, setEeaChecked] = useState<boolean>(processingRegions.includes("EEA_UK"));
  const [canadaChecked, setCanadaChecked] = useState<boolean>(processingRegions.includes("CANADA"));

  const updateProcessingRegions = async (region: ProcessingRegionEnum, checked: boolean) => {
    if (checked) {
      if (!processingRegions.some((r) => r == region)) {
        processingRegions.push(region);
      }
    } else {
      processingRegions = processingRegions.filter((r) => r !== region);
    }
    updateState(processingRegions);
  };

  return (
    <div className="ml-xxs flex-row align-items-center">
      <DescriptionCheckbox
        label={
          <Tooltip title="US">
            <img src="/assets/images/flag-usa-small.png" alt="US" width="40" height="25" />
          </Tooltip>
        }
        checked={usChecked}
        onChange={(e, checked) => {
          setUsChecked(checked);
          return updateProcessingRegions("UNITED_STATES", checked);
        }}
      />
      {organization && organization.featureGdpr && (
        <DescriptionCheckbox
          label={
            <Tooltip title="EEA/UK/CH">
              <img src="/assets/images/flag-eea.svg" alt="EEA/UK/CH" width="40" height="26" />
            </Tooltip>
          }
          checked={eeaChecked}
          onChange={(e, checked) => {
            setEeaChecked(checked);
            return updateProcessingRegions("EEA_UK", checked);
          }}
        />
      )}
      {organization && organization.featurePipeda && (
        <DescriptionCheckbox
          label={
            <Tooltip title="Canada">
              <img src="/assets/images/flag-canada.svg" alt="Canada" width="40" height="26" />
            </Tooltip>
          }
          checked={canadaChecked}
          onChange={(e, checked) => {
            setCanadaChecked(checked);
            return updateProcessingRegions("CANADA", checked);
          }}
        />
      )}
    </div>
  );
};
